import { ColorSchemeEnum, SectionEnum, UniformSelectionWriteSelectionTypeEnum } from "@sidegigllc/api/client";
import * as z from "zod/mini";

/**
 * UI constants for the Uniform feature (CORE-6180). The data types (`Uniform`, `UniformPreset`,
 * `UniformItem`, `SkillUniformOptions`, `ColorSchemeEnum`, `SectionEnum`,
 * `UniformSelectionWriteSelectionTypeEnum`) come from the generated client
 * (`GET /skills/{id}/uniform/`); this module only holds display copy.
 */

/** Human-facing copy for the enum values (Figma labels). */
export const COLOR_SCHEME_LABELS: Record<ColorSchemeEnum, string> = {
  BLACK_ON_BLACK: "Black on Black",
  WHITE_ON_BLACK: "White on Black",
};

export const UNIFORM_SECTION_LABELS: Record<SectionEnum, string> = {
  TOP: "Top",
  BOTTOM: "Bottom",
  FOOTWEAR: "Footwear",
  OTHER: "Other",
};

/** Colour options for the custom-builder toggle, in display order. */
export const COLOR_SCHEMES: ReadonlyArray<ColorSchemeEnum> = [
  ColorSchemeEnum.BLACK_ON_BLACK,
  ColorSchemeEnum.WHITE_ON_BLACK,
];

/** Garment sections in display order. */
export const SECTION_ORDER: ReadonlyArray<SectionEnum> = [
  SectionEnum.TOP,
  SectionEnum.BOTTOM,
  SectionEnum.FOOTWEAR,
  SectionEnum.OTHER,
];

/**
 * Copy for the explicit "no uniform required" selection (CORE-6537). FE owns the label and renders
 * `uniform_name_snapshot || NOT_REQUIRED_UNIFORM_LABEL`, so we're correct whether or not the backend
 * snapshots a name for the block.
 */
export const NOT_REQUIRED_UNIFORM_LABEL = "No uniform required";

/**
 * Read-only uniform block frozen onto a posted gig (backend CORE-6243), returned nullable on the
 * staffer gig-detail response. Unlike the write {@link uniformSelectionSchema}, this carries immutable
 * display strings (`*_snapshot`) — render verbatim, never re-resolve against the live catalog. The read
 * `selection_type` is a bare string in the spec; we narrow it to the write enum (PRESET/CUSTOM/NOT_REQUIRED).
 */
export interface UniformSnapshotItem {
  section: SectionEnum;
  label_snapshot: string;
}
export interface UniformSnapshot {
  selection_type: UniformSelectionWriteSelectionTypeEnum;
  /** `null` for CUSTOM and NOT_REQUIRED. */
  color_scheme: ColorSchemeEnum | null;
  uniform_name_snapshot: string;
  description_snapshot: string;
  items: Array<UniformSnapshotItem>;
}

// Shared discriminated-union variants for the uniform block. PRESET snapshots a catalog preset by
// `preset_id` (the backend composes the garments — client `item_ids` are ignored for PRESET); the
// CUSTOM base snapshots the chosen `item_ids` (global catalog + persisted per-location garments,
// CORE-6244).
const presetVariant = z.object({
  selection_type: z.literal(UniformSelectionWriteSelectionTypeEnum.PRESET),
  color_scheme: z.enum(ColorSchemeEnum),
  preset_id: z.number(),
});
const customBaseVariant = z.object({
  selection_type: z.literal(UniformSelectionWriteSelectionTypeEnum.CUSTOM),
  color_scheme: z.enum(ColorSchemeEnum),
  item_ids: z.array(z.number()),
});
// Explicit "no uniform required" (CORE-6537 / backend CORE-6455): carries no color_scheme, item_ids,
// or preset_id — the backend 400s on any of those. Shared by the create and template/edit schemas.
const notRequiredVariant = z.object({
  selection_type: z.literal(UniformSelectionWriteSelectionTypeEnum.NOT_REQUIRED),
});

/**
 * Write-only uniform block persisted on gig create (backend CORE-6240). The CUSTOM variant also carries
 * any free-text `custom_items` on top of the {@link customBaseVariant} `item_ids`.
 */
export const uniformSelectionSchema = z.discriminatedUnion("selection_type", [
  presetVariant,
  z.extend(customBaseVariant, {
    custom_items: z._default(z.array(z.object({ section: z.enum(SectionEnum), label: z.string() })), []),
  }),
  notRequiredVariant,
]);
export type UniformSelection = z.infer<typeof uniformSelectionSchema>;

/**
 * Uniform block persisted on a "Save as template" (CORE-6358, backend CORE-6242). Same variants as
 * {@link uniformSelectionSchema} but with **item_ids only** — templates store references, never
 * free-text `custom_items` (removed for templates), and pre-fill renders those refs against the fresh
 * catalog (the LIVE-reference contract). Feeding a gig-create block through this schema strips its
 * `custom_items`.
 */
export const templateUniformSelectionSchema = z.discriminatedUnion("selection_type", [
  presetVariant,
  customBaseVariant,
  notRequiredVariant,
]);
export type TemplateUniformSelection = z.infer<typeof templateUniformSelectionSchema>;
